import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RuleRoundedIcon from "@mui/icons-material/RuleRounded";
import SyncAltRoundedIcon from "@mui/icons-material/SyncAltRounded";
import api from "../axiosConfig";
import { AuthContext } from "../AuthContext";
import { createLine, listLines, updateLine } from "../services/whatsappLinesApi";
import MetaEmbeddedSignupDialog from "../components/whatsapp/MetaEmbeddedSignupDialog";

const ACCENT = "#2DB838";

const CARD_SX = {
  p: 2.5,
  borderRadius: 3,
  border: "1px solid rgba(148,163,184,0.15)",
  bgcolor: "white",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
};

const STATUS_META = {
  connected: { label: "Connected", color: "success" },
  pending: { label: "Needs link data", color: "warning" },
  offline: { label: "Inactive", color: "default" },
  reconnect_required: { label: "Reconnect needed", color: "warning" },
  disconnected: { label: "Disconnected", color: "default" },
};

const META_MODE_LABELS = {
  cloud_api_only: "API only",
  coexistence: "Coexistence",
};

const REPLY_POLICY_LABELS = {
  campaign_launcher_with_fallback: "Campaign launcher then fallback",
  rotation_owner: "Current rotation owner",
  line_owner: "Line owner",
  marketing_queue: "Marketing queue",
};

const blankForm = () => ({
  display_name: "",
  phone_number: "",
  line_type: "consultant",
  owner_user_id: "",
  fallback_owner_user_id: "",
  fallback_workstream_id: "",
  reply_policy: "line_owner",
  meta_mode: "coexistence",
  cloud_api_phone_number_id: "",
  cloud_api_business_account_id: "",
  strict_phone_number_id: false,
  is_active: true,
  members: [],
});

function defaultReplyPolicyForLineType(lineType = "") {
  switch (String(lineType || "").toLowerCase()) {
    case "broadcast":
    case "main":
      return "campaign_launcher_with_fallback";
    case "rotated_work":
      return "rotation_owner";
    default:
      return "line_owner";
  }
}

function defaultMetaModeForLineType(lineType = "") {
  switch (String(lineType || "").toLowerCase()) {
    case "broadcast":
    case "main":
      return "cloud_api_only";
    case "rotated_work":
    case "consultant":
    default:
      return "coexistence";
  }
}

function lineTypeLabel(lineType = "") {
  switch (String(lineType || "").toLowerCase()) {
    case "broadcast":
    case "main":
      return "Broadcast number";
    case "rotated_work":
      return "Rotated work line";
    default:
      return "Consultant / direct";
  }
}

function avatarColor(name = "") {
  const palette = ["#4f46e5", "#0ea5e9", "#16a34a", "#b45309", "#7c3aed", "#e11d48"];
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = name.charCodeAt(index) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

function initials(name = "") {
  return String(name || "?")
    .split(" ")
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function formatRelative(iso) {
  if (!iso) return "-";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function metaModeLabel(value = "") {
  return META_MODE_LABELS[String(value || "").toLowerCase()] || "API only";
}

function replyPolicyLabel(value = "") {
  return REPLY_POLICY_LABELS[String(value || "").toLowerCase()] || "Line owner";
}

function StatusChip({ status }) {
  const meta = STATUS_META[String(status || "").toLowerCase()] ?? { label: status || "Unknown", color: "default" };
  return <Chip label={meta.label} color={meta.color} size="small" sx={{ fontWeight: 700, fontSize: 11 }} />;
}

function MemberRow({ member, onRoleChange, onRemove }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: avatarColor(member.user?.name ?? "") }}>
        {initials(member.user?.name ?? "?")}
      </Avatar>
      <Typography flex={1} variant="body2" noWrap>
        {member.user?.name ?? `User #${member.user_id}`}
      </Typography>
      <FormControl size="small" sx={{ minWidth: 110 }}>
        <Select value={member.role || "backup"} onChange={(event) => onRoleChange(member.user_id, event.target.value)}>
          <MenuItem value="backup">Backup</MenuItem>
          <MenuItem value="manager">Manager</MenuItem>
          <MenuItem value="observer">Observer</MenuItem>
        </Select>
      </FormControl>
      <Button color="error" size="small" onClick={() => onRemove(member.user_id)} sx={{ textTransform: "none", minWidth: 0 }}>
        Remove
      </Button>
    </Stack>
  );
}

function LineDialog({ open, editingLine, allUsers, workstreams, onClose, onSaved }) {
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [addMemberUserId, setAddMemberUserId] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [lookupNote, setLookupNote] = useState(null);

  const isEdit = Boolean(editingLine);

  useEffect(() => {
    if (!open) return;

    if (editingLine) {
      setForm({
        display_name: editingLine.display_name ?? "",
        phone_number: editingLine.phone_number ?? "",
        line_type: editingLine.line_type ?? "consultant",
        owner_user_id: editingLine.owner?.id ?? "",
        fallback_owner_user_id: editingLine.fallback_owner?.id ?? "",
        fallback_workstream_id: editingLine.fallback_workstream?.id ?? "",
        reply_policy: editingLine.reply_policy ?? defaultReplyPolicyForLineType(editingLine.line_type),
        meta_mode: editingLine.meta_mode ?? defaultMetaModeForLineType(editingLine.line_type),
        cloud_api_phone_number_id: editingLine.cloud_api_phone_number_id ?? "",
        cloud_api_business_account_id: editingLine.cloud_api_business_account_id ?? "",
        strict_phone_number_id: Boolean(editingLine.strict_phone_number_id),
        is_active: editingLine.is_active ?? true,
        members: (editingLine.members ?? []).map((member) => ({
          user_id: member.user?.id,
          user: member.user,
          role: member.role ?? "backup",
          is_active: member.is_active ?? true,
        })),
      });
    } else {
      setForm(blankForm());
    }

    setError(null);
    setAddMemberUserId("");
  }, [editingLine, open]);

  const ownerIdNum = Number(form.owner_user_id) || null;
  const addedIds = new Set(form.members.map((member) => member.user_id));
  const availableToAdd = allUsers.filter((user) => user.id !== ownerIdNum && !addedIds.has(user.id));
  const availableFallbackOwners = allUsers.filter((user) => user.id !== ownerIdNum);

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleLineTypeChange = (nextType) => {
    setForm((current) => {
      const currentDefault = defaultReplyPolicyForLineType(current.line_type);
      const nextDefault = defaultReplyPolicyForLineType(nextType);
      const shouldShiftReplyPolicy = !current.reply_policy || current.reply_policy === currentDefault;
      const currentMetaDefault = defaultMetaModeForLineType(current.line_type);
      const nextMetaDefault = defaultMetaModeForLineType(nextType);
      const shouldShiftMetaMode = !current.meta_mode || current.meta_mode === currentMetaDefault;

      return {
        ...current,
        line_type: nextType,
        reply_policy: shouldShiftReplyPolicy ? nextDefault : current.reply_policy,
        meta_mode: shouldShiftMetaMode ? nextMetaDefault : current.meta_mode,
      };
    });
  };

  const addMember = () => {
    if (!addMemberUserId) return;
    const userId = Number(addMemberUserId);
    if (form.members.some((member) => member.user_id === userId)) return;
    const user = allUsers.find((entry) => entry.id === userId);
    setForm((current) => ({
      ...current,
      members: [
        ...current.members,
        {
          user_id: userId,
          user,
          role: "backup",
          is_active: true,
        },
      ],
    }));
    setAddMemberUserId("");
  };

  const removeMember = (userId) => {
    setForm((current) => ({
      ...current,
      members: current.members.filter((member) => member.user_id !== userId),
    }));
  };

  const changeRole = (userId, role) => {
    setForm((current) => ({
      ...current,
      members: current.members.map((member) =>
        member.user_id === userId ? { ...member, role } : member
      ),
    }));
  };

  const handleLookup = async () => {
    const phoneNumberId = form.cloud_api_phone_number_id.trim();
    if (!phoneNumberId) return;
    setLookingUp(true);
    setLookupError(null);
    setLookupNote(null);
    try {
      const response = await api.get("/omnichannel/whatsapp-lines/phone-number-lookup", {
        params: { phone_number_id: phoneNumberId },
      });
      const data = response.data?.data ?? {};
      setForm((current) => ({
        ...current,
        phone_number: data.display_phone_number ?? current.phone_number,
        cloud_api_business_account_id: data.waba_id ?? current.cloud_api_business_account_id,
        display_name: current.display_name || data.verified_name || current.display_name,
      }));
      if (!data.waba_id) {
        setLookupNote("Couldn't determine the WABA ID automatically — paste it from Meta Business Manager.");
      }
    } catch (err) {
      setLookupError(
        err?.response?.data?.message ?? "Could not look up that Phone Number ID. Check it is correct and the system token has access."
      );
    } finally {
      setLookingUp(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const payload = {
        display_name: form.display_name.trim(),
        phone_number: form.phone_number.trim(),
        line_type: form.line_type,
        owner_user_id: form.owner_user_id || null,
        fallback_owner_user_id: form.fallback_owner_user_id || null,
        fallback_workstream_id: form.fallback_workstream_id || null,
        reply_policy: form.reply_policy,
        meta_mode: form.meta_mode,
        cloud_api_phone_number_id: form.cloud_api_phone_number_id.trim(),
        cloud_api_business_account_id: form.cloud_api_business_account_id.trim() || null,
        strict_phone_number_id: Boolean(form.strict_phone_number_id),
        is_active: form.is_active,
        members: form.members.map((member) => ({
          user_id: member.user_id,
          role: member.role,
          is_active: true,
        })),
      };

      const saved = isEdit
        ? await updateLine(editingLine.id, payload)
        : await createLine(payload);

      onSaved(saved, isEdit);
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message
        ?? Object.values(requestError?.response?.data?.errors ?? {})[0]?.[0]
        ?? requestError?.message
        ?? "Unable to save the WhatsApp line.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const helperForReplyPolicy = form.line_type === "broadcast"
    ? "Broadcast replies can route back to the campaign launcher first, then fall back."
    : form.line_type === "rotated_work"
    ? "Use the duty roster so the active on-duty owner receives the rotating line."
    : "Direct lines usually route to the line owner.";

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 800 }}>
        {isEdit ? `Edit ${editingLine?.display_name}` : "Add WhatsApp Cloud API line"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} pt={0.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Display name"
            value={form.display_name}
            onChange={(event) => setField("display_name", event.target.value)}
            size="small"
            fullWidth
            required
          />

          <TextField
            label="Business phone number"
            value={form.phone_number}
            onChange={(event) => setField("phone_number", event.target.value)}
            size="small"
            fullWidth
            required
            placeholder="+263..."
            helperText="Use the exact business number linked in Meta."
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Line type</InputLabel>
              <Select
                value={form.line_type}
                label="Line type"
                onChange={(event) => handleLineTypeChange(event.target.value)}
              >
                <MenuItem value="consultant">Consultant / direct</MenuItem>
                <MenuItem value="broadcast">Broadcast number</MenuItem>
                <MenuItem value="rotated_work">Rotated work line</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Owner</InputLabel>
              <Select
                value={form.owner_user_id ?? ""}
                label="Owner"
                onChange={(event) => setField("owner_user_id", event.target.value)}
              >
                <MenuItem value="">- No owner -</MenuItem>
                {allUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Connection mode</InputLabel>
              <Select
                value={form.meta_mode}
                label="Connection mode"
                onChange={(event) => setField("meta_mode", event.target.value)}
              >
                <MenuItem value="cloud_api_only">API only</MenuItem>
                <MenuItem value="coexistence">Coexistence</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Reply policy</InputLabel>
              <Select
                value={form.reply_policy}
                label="Reply policy"
                onChange={(event) => setField("reply_policy", event.target.value)}
              >
                <MenuItem value="campaign_launcher_with_fallback">Campaign launcher then fallback</MenuItem>
                <MenuItem value="rotation_owner">Current rotation owner</MenuItem>
                <MenuItem value="line_owner">Line owner</MenuItem>
                <MenuItem value="marketing_queue">Marketing queue</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Alert severity={form.meta_mode === "coexistence" ? "info" : "success"} icon={<SyncAltRoundedIcon />}>
            {form.meta_mode === "coexistence"
              ? "This line is marked for phone + Unibox coexistence. Keep the linked Phone Number ID unique per number."
              : "This line is marked as API-only. That is usually the cleanest setup for broadcast-only numbers."}
          </Alert>

          <Alert severity="info" icon={<RuleRoundedIcon />}>
            {helperForReplyPolicy}
          </Alert>

          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Phone Number ID"
                value={form.cloud_api_phone_number_id}
                onChange={(event) => {
                  setField("cloud_api_phone_number_id", event.target.value);
                  setLookupError(null);
                  setLookupNote(null);
                }}
                size="small"
                fullWidth
                required
                helperText="Copy this from Meta Business Manager → WABA → Phone Numbers."
              />
              <Button
                variant="outlined"
                size="small"
                disabled={!form.cloud_api_phone_number_id.trim() || lookingUp}
                onClick={handleLookup}
                sx={{ mt: 0.5, whiteSpace: "nowrap", minWidth: 90 }}
                startIcon={lookingUp ? <CircularProgress size={13} color="inherit" /> : null}
              >
                {lookingUp ? "Looking…" : "Lookup"}
              </Button>
            </Stack>
            {lookupError ? (
              <Alert severity="error" sx={{ py: 0.5 }}>{lookupError}</Alert>
            ) : null}
            {lookupNote ? (
              <Alert severity="info" sx={{ py: 0.5 }}>{lookupNote}</Alert>
            ) : null}
          </Stack>

          <TextField
            label="Business Account ID (WABA ID)"
            value={form.cloud_api_business_account_id}
            onChange={(event) => setField("cloud_api_business_account_id", event.target.value)}
            size="small"
            fullWidth
            helperText="Auto-filled by Lookup, or paste from Meta Business Manager."
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Fallback owner</InputLabel>
              <Select
                value={form.fallback_owner_user_id ?? ""}
                label="Fallback owner"
                onChange={(event) => setField("fallback_owner_user_id", event.target.value)}
              >
                <MenuItem value="">- No fallback owner -</MenuItem>
                {availableFallbackOwners.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Fallback workstream</InputLabel>
              <Select
                value={form.fallback_workstream_id ?? ""}
                label="Fallback workstream"
                onChange={(event) => setField("fallback_workstream_id", event.target.value)}
              >
                <MenuItem value="">- No fallback workstream -</MenuItem>
                {workstreams.map((workstream) => (
                  <MenuItem key={workstream.id} value={workstream.id}>
                    {workstream.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
              <Switch
                checked={form.is_active}
                onChange={(event) => setField("is_active", event.target.checked)}
                size="small"
              />
              <Box>
                <Typography variant="body2">Line is active</Typography>
                <Typography variant="caption" color="text.secondary">
                  Inactive lines stay saved but are treated as offline.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
              <Switch
                checked={form.strict_phone_number_id}
                onChange={(event) => setField("strict_phone_number_id", event.target.checked)}
                size="small"
              />
              <Box>
                <Typography variant="body2">Strict Phone Number ID</Typography>
                <Typography variant="caption" color="text.secondary">
                  Require this line's exact Meta Phone Number ID for routing.
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
              Backup and coverage members
            </Typography>

            <Stack spacing={1.5}>
              {form.members.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No additional coverage members yet.
                </Typography>
              ) : (
                form.members.map((member) => (
                  <MemberRow
                    key={member.user_id}
                    member={member}
                    onRoleChange={changeRole}
                    onRemove={removeMember}
                  />
                ))
              )}
            </Stack>

            <Stack direction="row" spacing={1} mt={2} alignItems="center">
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Add member</InputLabel>
                <Select
                  value={addMemberUserId}
                  label="Add member"
                  onChange={(event) => setAddMemberUserId(event.target.value)}
                >
                  <MenuItem value="">- Select user -</MenuItem>
                  {availableToAdd.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" size="small" disabled={!addMemberUserId} onClick={addMember} startIcon={<AddIcon />}>
                Add
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            saving
            || !form.display_name.trim()
            || !form.phone_number.trim()
            || !form.cloud_api_phone_number_id.trim()
          }
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CloudDoneRoundedIcon />}
          sx={{ bgcolor: ACCENT, "&:hover": { bgcolor: "#1fa82b" }, fontWeight: 700 }}
        >
          {isEdit ? "Save line" : "Create line"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LineCard({ line, canEdit, onEdit }) {
  return (
    <Paper sx={CARD_SX}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Avatar sx={{ bgcolor: avatarColor(line.display_name), width: 44, height: 44, fontWeight: 700, fontSize: 16 }}>
          {initials(line.display_name)}
        </Avatar>

        <Box flex={1} minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography fontWeight={800} fontSize={15} noWrap>
              {line.display_name}
            </Typography>
            <StatusChip status={line.session_status} />
            <Chip
              label={lineTypeLabel(line.line_type)}
              size="small"
              variant="outlined"
              sx={{ fontSize: 11 }}
            />
            <Chip label={metaModeLabel(line.meta_mode)} size="small" color="success" variant="outlined" sx={{ fontSize: 11 }} />
            <Chip label={replyPolicyLabel(line.reply_policy)} size="small" variant="outlined" sx={{ fontSize: 11 }} />
            {line.strict_phone_number_id ? (
              <Chip label="Strict ID" size="small" color="warning" variant="outlined" sx={{ fontSize: 11 }} />
            ) : null}
            {line.meta_onboarding?.verification_status ? (
              <Chip
                label={`Meta ${String(line.meta_onboarding.verification_status).replace(/_/g, " ")}`}
                size="small"
                color={String(line.meta_onboarding.verification_status).toUpperCase() === "VERIFIED" ? "success" : "warning"}
                variant="outlined"
                sx={{ fontSize: 11 }}
              />
            ) : null}
          </Stack>

          <Stack direction="row" spacing={2} mt={0.75} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              {line.phone_number || "No phone number"}
            </Typography>
            {line.owner ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PersonRoundedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                <Typography variant="body2" color="text.secondary">
                  {line.owner.name}
                </Typography>
              </Stack>
            ) : null}
            {line.members?.length ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <GroupRoundedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                <Typography variant="body2" color="text.secondary">
                  {line.members.length} member{line.members.length !== 1 ? "s" : ""}
                </Typography>
              </Stack>
            ) : null}
          </Stack>

          <Stack spacing={0.4} mt={1}>
            <Typography variant="caption" color="text.secondary">
              Phone number ID: {line.cloud_api_phone_number_id || "Not linked yet"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Business account ID: {line.cloud_api_business_account_id || "Not set"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fallback: {line.fallback_owner?.name || line.fallback_workstream?.name || "No fallback configured"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Coexistence: {line.meta_mode === "coexistence" ? `Enabled ${formatRelative(line.coexistence_enabled_at)}` : "Not enabled"}
            </Typography>
            {line.meta_onboarding ? (
              <Typography variant="caption" color="text.secondary">
                Embedded signup: {line.meta_onboarding.status || "completed"} - Verification {line.meta_onboarding.verification_status || "Unknown"}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              Last seen {formatRelative(line.last_seen_at)} - {line.open_conversations_count || 0} open conversation{Number(line.open_conversations_count || 0) === 1 ? "" : "s"}
            </Typography>
          </Stack>
        </Box>

        {canEdit ? (
          <IconButton size="small" onClick={() => onEdit(line)}>
            <EditRoundedIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default function WhatsAppLinesPage({ embedded = false }) {
  const { user } = useContext(AuthContext);
  const [lines, setLines] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [workstreams, setWorkstreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [toast, setToast] = useState(null);
  const [signupOpen, setSignupOpen] = useState(false);

  const isAdmin = Boolean(user?.is_admin) || ["admin", "superadmin"].includes(String(user?.role || "").toLowerCase());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [lineData, userResponse, workstreamResponse] = await Promise.all([
        listLines(),
        isAdmin ? api.get("/admin/users") : Promise.resolve({ data: [] }),
        isAdmin
          ? api.get("/omnichannel/workstreams").catch(() => ({ data: { data: [] } }))
          : Promise.resolve({ data: { data: [] } }),
      ]);

      setLines(Array.isArray(lineData) ? lineData : []);
      setAllUsers(Array.isArray(userResponse.data) ? userResponse.data : []);
      setWorkstreams(Array.isArray(workstreamResponse?.data?.data) ? workstreamResponse.data.data : []);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError?.message || "Unable to load WhatsApp lines.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  const sortedLines = useMemo(
    () =>
      [...lines].sort((left, right) => {
        const leftConnected = String(left?.session_status || "").toLowerCase() === "connected" ? 1 : 0;
        const rightConnected = String(right?.session_status || "").toLowerCase() === "connected" ? 1 : 0;
        if (rightConnected !== leftConnected) {
          return rightConnected - leftConnected;
        }

        return String(left?.display_name || "").localeCompare(String(right?.display_name || ""));
      }),
    [lines]
  );

  const handleSaved = (savedLine, wasEdit) => {
    setDialogOpen(false);
    setEditingLine(null);
    setToast(wasEdit ? "WhatsApp line updated." : "WhatsApp line created.");
    setLines((current) =>
      wasEdit
        ? current.map((line) => (line.id === savedLine.id ? savedLine : line))
        : [...current, savedLine]
    );
  };

  return (
    <Box sx={{ minHeight: embedded ? "auto" : "100vh", bgcolor: embedded ? "transparent" : "#f6f8fa", p: embedded ? 0 : { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={3} flexWrap="wrap">
        <Box flex={1}>
          <Typography variant="h5" fontWeight={900}>
            WhatsApp Cloud API Lines
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage consultant and broadcast numbers registered under your WABA. Add each number's Phone Number ID from Meta Business Manager.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <IconButton onClick={() => void load()} disabled={loading}>
            <RefreshRoundedIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<SyncAltRoundedIcon />}
            onClick={() => setSignupOpen(true)}
            sx={{ borderColor: ACCENT, color: ACCENT, fontWeight: 700, "&:hover": { borderColor: "#1fa82b", bgcolor: "rgba(45,184,56,0.06)" } }}
          >
            Connect existing number
          </Button>
          {isAdmin ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingLine(null);
                setDialogOpen(true);
              }}
              sx={{ bgcolor: ACCENT, "&:hover": { bgcolor: "#1fa82b" }, fontWeight: 700 }}
            >
              Add line
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Each consultant number should be registered under your WABA in Meta Business Manager first. Then add it here using its Phone Number ID — click Lookup to auto-fill the rest. Set Connection Mode to Coexistence to keep the consultant's WhatsApp Business app working alongside the unibox.
      </Alert>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" pt={8}>
          <CircularProgress sx={{ color: ACCENT }} />
        </Box>
      ) : sortedLines.length === 0 ? (
        <Paper sx={{ ...CARD_SX, textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">No Cloud API WhatsApp lines configured yet.</Typography>
          {isAdmin ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingLine(null);
                setDialogOpen(true);
              }}
              sx={{ mt: 2, bgcolor: ACCENT, "&:hover": { bgcolor: "#1fa82b" } }}
            >
              Add first line
            </Button>
          ) : null}
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {sortedLines.map((line) => (
            <LineCard
              key={line.id}
              line={line}
              canEdit={isAdmin}
              onEdit={(selectedLine) => {
                setEditingLine(selectedLine);
                setDialogOpen(true);
              }}
            />
          ))}
        </Stack>
      )}

      <LineDialog
        open={dialogOpen}
        editingLine={editingLine}
        allUsers={allUsers}
        workstreams={workstreams}
        onClose={() => {
          setDialogOpen(false);
          setEditingLine(null);
        }}
        onSaved={handleSaved}
      />

      <MetaEmbeddedSignupDialog
        open={signupOpen}
        onClose={() => setSignupOpen(false)}
        allUsers={allUsers}
        currentUser={user}
        isAdmin={isAdmin}
        onConnected={() => {
          setToast("WhatsApp Business number connected for coexistence.");
          void load();
        }}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setToast(null)} severity="success" sx={{ width: "100%" }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
